import React from 'react';
import { cn } from '@/lib/utils';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, XCircle } from 'lucide-react';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
	type?: AlertType;
	title?: string;
	message: string;
	className?: string;
	icon?: boolean;
	closeable?: boolean;
	onClose?: () => void;
}

const alertStyles = {
	success: {
		container: 'bg-emerald-50 border-emerald-200 text-emerald-900',
		icon: 'text-emerald-600',
		title: 'text-emerald-900 font-semibold',
	},
	error: {
		container: 'bg-rose-50 border-rose-200 text-rose-900',
		icon: 'text-rose-600',
		title: 'text-rose-900 font-semibold',
	},
	warning: {
		container: 'bg-amber-50 border-amber-200 text-amber-900',
		icon: 'text-amber-600',
		title: 'text-amber-900 font-semibold',
	},
	info: {
		container: 'bg-blue-50 border-blue-200 text-blue-900',
		icon: 'text-blue-600',
		title: 'text-blue-900 font-semibold',
	},
};

const iconMap = {
	success: CheckCircle2,
	error: XCircle,
	warning: AlertTriangle,
	info: Info,
};

export const Alert = ({
	type = 'info',
	title,
	message,
	className,
	icon = true,
	closeable = false,
	onClose,
}: AlertProps) => {
	const style = alertStyles[type];
	const Icon = iconMap[type];
	const [isOpen, setIsOpen] = React.useState(true);

	if (!isOpen) return null;

	const handleClose = () => {
		setIsOpen(false);
		onClose?.();
	};

	return (
		<div
			className={cn(
				'rounded-lg border px-4 py-4 flex items-start gap-3',
				style.container,
				className
			)}
		>
			{icon && <Icon className={cn('h-5 w-5 mt-0.5 shrink-0', style.icon)} />}
			<div className="flex-1">
				{title && <p className={style.title}>{title}</p>}
				<p className="text-sm mt-1">{message}</p>
			</div>
			{closeable && (
				<button
					onClick={handleClose}
					className="text-foreground/50 hover:text-foreground transition-colors shrink-0"
				>
					<XCircle className="h-5 w-5" />
				</button>
			)}
		</div>
	);
};

// Status Badge Component
export type StatusType = 'active' | 'inactive' | 'pending' | 'error' | 'success';

interface StatusBadgeProps {
	status: StatusType;
	label: string;
	className?: string;
}

const statusStyles = {
	active: 'bg-emerald-100 text-emerald-900 border-emerald-300',
	inactive: 'bg-gray-100 text-gray-900 border-gray-300',
	pending: 'bg-amber-100 text-amber-900 border-amber-300',
	error: 'bg-rose-100 text-rose-900 border-rose-300',
	success: 'bg-emerald-100 text-emerald-900 border-emerald-300',
};

export const StatusBadge = ({ status, label, className }: StatusBadgeProps) => {
	return (
		<span
			className={cn(
				'px-2 py-1 text-xs font-medium rounded-full border',
				statusStyles[status],
				className
			)}
		>
			{label}
		</span>
	);
};

// Empty State Component
interface EmptyStateProps {
	icon?: React.ReactNode;
	title: string;
	description?: string;
	action?: React.ReactNode;
	className?: string;
}

export const EmptyState = ({
	icon,
	title,
	description,
	action,
	className,
}: EmptyStateProps) => {
	return (
		<div className={cn('flex flex-col items-center justify-center py-12 px-4', className)}>
			{icon && <div className="mb-4 text-muted-foreground">{icon}</div>}
			<h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
			{description && <p className="text-muted-foreground text-center mb-6 max-w-sm">{description}</p>}
			{action && <div>{action}</div>}
		</div>
	);
};

// Loading Skeleton
interface SkeletonProps {
	className?: string;
}

export const Skeleton = ({ className }: SkeletonProps) => {
	return (
		<div
			className={cn(
				'animate-pulse rounded-md bg-muted',
				className
			)}
		/>
	);
};

// Grid Layout Helper
interface GridProps {
	children: React.ReactNode;
	cols?: 1 | 2 | 3 | 4;
	gap?: 'sm' | 'md' | 'lg';
	className?: string;
}

export const Grid = ({ children, cols = 3, gap = 'md', className }: GridProps) => {
	const colsClass = {
		1: 'grid-cols-1',
		2: 'md:grid-cols-2',
		3: 'md:grid-cols-2 lg:grid-cols-3',
		4: 'md:grid-cols-2 lg:grid-cols-4',
	}[cols];

	const gapClass = {
		sm: 'gap-4',
		md: 'gap-6',
		lg: 'gap-8',
	}[gap];

	return (
		<div className={cn('grid', colsClass, gapClass, className)}>
			{children}
		</div>
	);
};
